import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CommentIcon from '@mui/icons-material/Comment';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiKey } from '../../contexts/ApiKeyContext';
import { Message, MessageThread, postMessage } from '../../services/MessagingService';
import { getThreadTitle } from '../../utils/CommunicatorUtils';
import { getShortDateTimeFormat } from '../../utils/Dates';
import { MessageBubble } from './MessageBubble';

const COLLAPSED_DRAWER_WIDTH = 50;
const EXPANDED_DRAWER_WIDTH = 240;
const WIDGET_DRAWER_WIDTH = 300;

interface InboxProps {
  threads: MessageThread[];
  mode: 'widget' | 'static';
  onNewMessage?: (newMessage: Message, threadId: number) => void;
  onClose?: () => void;
  open?: boolean;
  openThread: (threadId: number) => void;
  selectedThreadId?: number;
  msgPreview?: boolean;
  onCollapse?: (width: number) => void;
  onExpand?: (width: number) => void;
}

export const Inbox = ({
  threads,
  mode,
  onNewMessage,
  onClose,
  open,
  openThread,
  selectedThreadId,
  msgPreview = true,
  onCollapse,
  onExpand,
}: InboxProps) => {
  const { apiKey, userProfile } = useApiKey();
  const navigate = useNavigate();

  const [replyingThreadId, setReplyingThreadId] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (collapsed && onCollapse) onCollapse(COLLAPSED_DRAWER_WIDTH);
    else onExpand && onExpand(EXPANDED_DRAWER_WIDTH);
  }, [collapsed]);

  // Extracted reusable function for sending a message
  const handleSendMessage = async (threadId: number) => {
    try {
      setIsSending(true);
      setSendError(null);
      const newMessage = await postMessage(apiKey, threadId, replyContent);
      onNewMessage && onNewMessage(newMessage, threadId);
      setReplyingThreadId(null);
      setReplyContent('');
    } catch (err) {
      console.error('Failed to send message:', err);
      setSendError('Nie udało się wysłać wiadomości.');
    } finally {
      setIsSending(false);
    }
  };

  const drawerSx =
    mode === 'static'
      ? {
          '& .MuiDrawer-paper': {
            top: 'unset',
            boxSizing: 'border-box',
            maxHeight: `calc(100vh - ${document.getElementById('navbar')?.clientHeight ?? 60}px - 20px - ${document.getElementById('footer')?.clientHeight ?? 60}px)`,
          },
        }
      : undefined;

  return (
    <Drawer
      anchor={mode === 'widget' ? 'right' : 'left'}
      open={open}
      variant="persistent"
      ModalProps={{
        keepMounted: true,
      }}
      sx={drawerSx}
    >
      <Box
        sx={{
          width: mode === 'widget' ? WIDGET_DRAWER_WIDTH : collapsed ? COLLAPSED_DRAWER_WIDTH : EXPANDED_DRAWER_WIDTH,
          p: 1.5,
          transition: 'width 0.3s ease-in-out',
          overflowX: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'center', mb: 1.5 }}>
          {mode === 'widget' ? (
            <>
              <MailOutlineIcon fontSize="large" />
              <Button
                size="small"
                onClick={() => {
                  navigate('messages');
                  onClose && onClose();
                }}
              >
                Przejdź do skrzynki
              </Button>
              <Button size="small" onClick={onClose} variant="outlined">
                Zamknij
              </Button>
            </>
          ) : (
            <>
              {!collapsed && <Typography>Wątki</Typography>}
              <IconButton onClick={() => setCollapsed(!collapsed)}>
                {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </>
          )}
        </Box>
        {threads.length === 0 ? (
          <Typography variant="body2">Brak wiadomości</Typography>
        ) : (
          threads.map((thread, index) => (
            <Tooltip key={thread.id} arrow title={collapsed ? getThreadTitle(thread) : undefined} placement="right">
              <Card
                tabIndex={0}
                role="button"
                aria-label={`Otwórz wątek ${thread.title || thread.id}`}
                onClick={() => {
                  if (mode === 'static') {
                    openThread(thread.id);
                    if (window.innerWidth < 768) setCollapsed(true);
                  }
                }}
                onKeyDown={(e) => {
                  if ((mode === 'static' && e.key === 'Enter') || e.key === ' ') {
                    e.preventDefault();
                    openThread(thread.id);
                    if (window.innerWidth < 768) setCollapsed(true);
                  }
                }}
                sx={{
                  borderBottom: '1px solid #eee',
                  borderLeft: thread.unreadCount > 0 ? '4px solid #1976d2' : '4px solid transparent',
                  py: 1,
                  px: 0.75,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 1,
                  my: 1,
                  cursor: 'pointer',
                  backgroundColor: thread.id === selectedThreadId ? '#e3f2fd' : 'inherit',
                  '&:hover': {
                    backgroundColor: thread.id === selectedThreadId ? '#e3f2fd' : '#f9f9f9',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '97%' }}>
                  <Typography sx={{ fontWeight: thread.unreadCount > 0 ? 'bold' : 'normal' }}>
                    {collapsed ? `${index + 1}` : getThreadTitle(thread)}
                  </Typography>
                  {thread.unreadCount > 0 && <Badge color="primary" variant="dot" />}
                </Box>
                {thread.lastMessage ? (
                  <>
                    <Stack direction="row" justifyContent="space-between" width="100%">
                      <Typography variant="caption">{getShortDateTimeFormat(thread.lastMessage.sentAt, true)}</Typography>
                      {!collapsed && (
                        <Typography variant="body2">
                          {thread.lastMessage.senderName} {thread.lastMessage.senderId === userProfile?.id && '(Ty)'}
                        </Typography>
                      )}
                    </Stack>
                    {msgPreview && <MessageBubble message={thread.lastMessage} />}
                  </>
                ) : (
                  <>
                    {msgPreview && (
                      <Typography variant="body2" gutterBottom>
                        Brak wiadomości w wątku
                      </Typography>
                    )}
                  </>
                )}
                {mode === 'widget' && (
                  <Box sx={{ alignSelf: 'flex-end' }}>
                    {replyingThreadId === null && (
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          setReplyingThreadId((current) => (current === thread.id ? null : thread.id));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setReplyingThreadId((current) => (current === thread.id ? null : thread.id));
                          }
                        }}
                        startIcon={<CommentIcon />}
                      >
                        Odpowiedz
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openThread(thread.id)}
                      sx={{ m: 1 }}
                      endIcon={<ChevronRightIcon />}
                    >
                      Do wątku
                    </Button>
                  </Box>
                )}
                {replyingThreadId === thread.id && (
                  <Stack spacing={1} sx={{ width: '100%', alignItems: 'flex-end' }}>
                    <TextField
                      value={replyContent}
                      autoFocus
                      onChange={(e) => setReplyContent(e.target.value)}
                      label={`Odpowiedź do: ${getThreadTitle(thread)}`}
                      placeholder="Nowa wiadomość..."
                      size="small"
                      fullWidth
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' && !e.shiftKey && replyContent !== '') {
                          e.preventDefault();
                          await handleSendMessage(thread.id);
                        }
                      }}
                    />
                    {sendError && (
                      <Typography color="error" variant="caption">
                        {sendError}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end' }}>
                      <Button
                        onClick={async (e) => {
                          e.stopPropagation();
                          await handleSendMessage(thread.id);
                        }}
                        variant="contained"
                        size="small"
                        disabled={replyContent === '' || isSending}
                      >
                        {isSending ? 'Wysyłanie...' : 'Wyślij'}
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setReplyingThreadId(null);
                        }}
                        variant="outlined"
                        size="small"
                      >
                        Anuluj
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Card>
            </Tooltip>
          ))
        )}
      </Box>
    </Drawer>
  );
};
